<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import VuePdfEmbed from 'vue-pdf-embed';
import { pdfSource } from '@/utils/pdf-source.util';
import { useResizeObserver } from '@vueuse/core';
import { debounce } from 'radash';
import type { ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';

import MobileScoreBlurb from '@/components/Mobile/Conversation/MobileScoreBlurb.vue';
import { useSignedResumeUrl } from '@/composables/useSignedResumeUrl';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import ProjectService from '@/core/shared/project/project.service';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import { hasApplicationScore } from '@/core/conversations/application-score/application-score.util';
import { getJobTitle } from '@/core/sourcing/utils/job';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';

const proProfileService = new ProProfileService();
const applicationScoreService = new ApplicationScoreService();
const projectService = new ProjectService();

const pdfWrapperRef = ref<HTMLElement | null>(null);
const pdfWidth = ref(0);
const hasError = ref(false);
const scoreDetail = ref<ATSApplicationScoringProScoring>();

// The conversation selection (useJobsRouting -> selectConversationIndex) loads the
// pro profile before navigation, so the resume tab reads it straight from the store.
const profile = computed(() => proProfileService.selectedProProfile);
const resumeUrl = computed(() => profile.value?.pro?.resumeUrl);
const selectedApplication = computed(() => proProfileService.selectedProApplication);

const { signedUrl: signedResumeUrl, error: signedUrlError } = useSignedResumeUrl(resumeUrl);

const jobTitle = computed(() =>
  selectedApplication.value?.job
    ? getJobTitle(selectedApplication.value.job, false).displayTitle
    : '',
);

// Resolve the Copilot display name from the loaded project, mirroring the
// conversation thread. getLocalProjectByJobId is a synchronous store lookup.
const aiRecruiterName = computed(() => {
  const jobId = selectedApplication.value?.jobId;
  const project = jobId ? projectService.getLocalProjectByJobId(jobId) : null;
  return ProjectCopilotConfigurationUtils.getCopilotName(project);
});

const scoreChips = computed(() => ApplicationScoreService.getScoreChipsInfo(scoreDetail.value));

// Match the desktop resume tab: only show the blurb once a real score has loaded.
const showScoreBlurb = computed(
  () =>
    !!selectedApplication.value?.id &&
    hasApplicationScore(scoreDetail.value?.score) &&
    !!scoreDetail.value?.summary,
);

function handlePdfError(): void {
  hasError.value = true;
  SnackbarService.critical('Error loading resume. Please try again later.');
}

watch(signedUrlError, (error) => {
  if (error) {
    handlePdfError();
  }
});

watch(
  () => selectedApplication.value?.id,
  async (applicationId) => {
    if (!applicationId) {
      scoreDetail.value = undefined;
      return;
    }
    try {
      const detail = await applicationScoreService.getScoreDetail(applicationId);
      // Drop a stale response: the selected application may have changed while the
      // request was in flight, so only apply it if this id is still the active one.
      if (applicationId === selectedApplication.value?.id) {
        scoreDetail.value = detail;
      }
    } catch (error) {
      ErrorService.captureException(error);
      SnackbarService.critical('Could not load the score details.');
    }
  },
  { immediate: true },
);

function measurePdfWidth(): void {
  pdfWidth.value = pdfWrapperRef.value?.offsetWidth ?? 0;
}

// Debounce resize-driven width updates (mirrors the desktop resume tab). When the
// score blurb loads in asynchronously the layout reflows and a scrollbar can
// appear, briefly changing the wrapper width. Applying that mid-load would churn
// VuePdfEmbed and trip its loading-failed handler (which latches the error state),
// so let the width settle before updating.
const updatePdfWidth = debounce({ delay: 300 }, measurePdfWidth);

// Registered at setup scope (not inside onMounted) so VueUse ties the observer to
// the component's effect scope and re-measures when the container first mounts
// (e.g. when the profile loads after the resume tab is already open).
useResizeObserver(pdfWrapperRef, updatePdfWidth);

onMounted(() => {
  measurePdfWidth();
});
</script>

<template>
  <div class="flex flex-col gap-4 overflow-y-auto bg-tint-40 p-3" data-test="mobile-resume">
    <MobileScoreBlurb
      v-if="showScoreBlurb"
      :application-id="selectedApplication?.id ?? 0"
      :job-title="jobTitle"
      :ai-recruiter-name="aiRecruiterName"
      :score="scoreDetail?.score"
      :content="scoreDetail?.summary"
      :chips="scoreChips"
    />

    <div
      v-if="resumeUrl"
      ref="pdfWrapperRef"
      class="relative shrink-0 overflow-hidden rounded-xl border border-tint-60 bg-tint-0"
      data-test="mobile-resume-pdf"
    >
      <p
        v-if="hasError"
        class="p-4 text-center text-sm text-shade-800"
        data-test="mobile-resume-error"
      >
        Couldn't load the resume. Please try again later.
      </p>
      <template v-else-if="signedResumeUrl && pdfWidth">
        <VuePdfEmbed
          :source="pdfSource(signedResumeUrl)"
          :width="pdfWidth"
          class="sensitive"
          @loading-failed="handlePdfError"
          @rendering-failed="handlePdfError"
        />
      </template>
      <v-skeleton-loader v-else boilerplate type="article, subtitle, article, article, paragraph" />
    </div>

    <div
      v-else-if="profile"
      class="flex flex-1 flex-col items-center justify-center"
      data-test="mobile-resume-empty"
    >
      <img src="@/assets/svg/no-resume.svg" alt="" />
      <p class="mb-2 mt-4 text-sm font-bold text-shade-800">No resume available</p>
      <p class="w-60 text-center text-sm text-shade-800">
        This candidate doesn't have a resume yet.
      </p>
    </div>

    <v-skeleton-loader v-else boilerplate type="article, subtitle, article, article, paragraph" />
  </div>
</template>
